import React, { useState, useRef, useEffect } from 'react';
import { IonModal, IonHeader, IonToolbar, IonButton, IonItem, IonIcon, IonLabel, IonText, IonList, IonChip, IonBadge, IonSearchbar, IonCardContent, createAnimation } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import styles from './SheetModal.module.scss';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { useSheetScrollGuard } from '../hooks/useSheetScrollGuard';

// Custom modal-leave animation. After a DRAG close the sheet gesture has already
// slid the sheet fully off (currentBreakpoint 0) and the wrapper is at
// translateY(100%). Ionic's default leave then runs a ~200ms animation that
// re-sets that same transform + fades an already-invisible backdrop — a
// one-frame repaint you feel as a hitch at the very end. When already down, do
// literally nothing (fade the backdrop instantly, no wrapper keyframes at all).
// For a backdrop/X close (currentBreakpoint 1) keep a real 200ms slide + fade.
const sheetLeaveAnimation = (baseEl: HTMLElement, opts: { currentBreakpoint?: number }) => {
  const root = (baseEl as HTMLElement & { shadowRoot?: ShadowRoot }).shadowRoot ?? baseEl;
  const bp = opts?.currentBreakpoint ?? 1;
  const alreadyDown = bp <= 0.01;

  const backdrop = createAnimation()
    .addElement(root.querySelector('ion-backdrop')!)
    .fromTo('opacity', `calc(var(--backdrop-opacity) * ${bp})`, '0');

  const anims = [backdrop];
  if (!alreadyDown) {
    anims.push(
      createAnimation()
        .addElement(root.querySelector('.modal-wrapper')!)
        .keyframes([
          { offset: 0, opacity: 1, transform: `translateY(${100 - bp * 100}%)` },
          { offset: 1, opacity: 1, transform: 'translateY(100%)' },
        ]),
    );
  }

  return createAnimation()
    .easing('cubic-bezier(0.47,0,0.745,0.715)')
    .duration(alreadyDown ? 0 : 200)
    .addAnimation(anims);
};

interface Option {
  label: string;
  icon?: string;
  onClick: () => void;
  labelContent?: React.ReactNode;
  showChip?: boolean;
  color?: string; // for IonIcon color
  iconColor?: string; // for IonIcon color
  rightIcon?: string; // for right side icon (like checkmark)
  customContent?: React.ReactNode; // for custom rendering like country flags
}

interface HeaderBadge {
  label: string;
  color: string;
}

// Maps an Ionic color name to the matching muted/glass badge tone class below —
// avoids Ionic's `color` prop, whose shadow CSS (:host(.ion-color)) sets
// background/color directly and ignores the --background/--color custom properties.
const BADGE_TONE_CLASS: Record<string, string> = {
  success: 'badgeSuccess',
  danger: 'badgeDanger',
  warning: 'badgeWarning',
  primary: 'badgePrimary',
  tertiary: 'badgeTertiary',
  medium: 'badgeMedium',
};

interface SheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  menuOptions: Option[];
  showChips?: boolean;
  additionalContent?: React.ReactNode;
  // Rich header (badge + title + subtitle) shown instead of the plain `title` text
  headerBadge?: HeaderBadge;
  headerTitle?: string;
  headerSubtitle?: string;
  // Selector-specific props
  isSelector?: boolean;
  selectorItems?: string[];
  onSelectorItemClick?: (item: string) => void;
  searchPlaceholder?: string;
  renderSelectorItem?: (item: string, index: number) => React.ReactNode;
  // Optional icon button in the plain-title header, left of the close X (e.g. "add contact")
  headerAction?: { icon: string; label: string; onClick: () => void };
  // Selector mode: own search matching (default: case-insensitive substring) and a cap on how many matches render
  filterSelectorItem?: (item: string, searchTerm: string) => boolean;
  selectorResultCap?: (hasSearch: boolean) => number;
  // Selector mode: shown when nothing matches (or there is nothing to list yet)
  selectorEmptyText?: string;
}

const extractChipInfo = (labelContent: React.ReactNode): { count: string; color: string } => {
  if (!labelContent) return { count: '0', color: 'primary' };
  
  let foundCount = '0';
  let foundColor = 'primary';
  
  const searchForInfo = (element: React.ReactNode): void => {
    if (typeof element === 'string' || typeof element === 'number') {
      const match = String(element).match(/(\d+)/);
      if (match && foundCount === '0') {
        foundCount = match[1];
      }
    }
    
    if (React.isValidElement(element)) {
      const props = element.props as React.HTMLAttributes<HTMLElement> & { color?: string; children?: React.ReactNode };
      if (props?.color) {
        foundColor = props.color;
      }
      
      if (props?.children) {
        const children = Array.isArray(props.children) 
          ? props.children 
          : [props.children];
        
        for (const child of children) {
          searchForInfo(child);
        }
      }
    }
    
    if (Array.isArray(element)) {
      for (const item of element) {
        searchForInfo(item);
      }
    }
  };
  
  searchForInfo(labelContent);
  return { count: foundCount, color: foundColor };
};

const SheetModal: React.FC<SheetModalProps> = (props) => {
  const {
    isOpen,
    onClose,
    onSelectorItemClick,
    searchPlaceholder = "Search...",
    renderSelectorItem,
  } = props;

  const {
    title,
    menuOptions,
    showChips = false,
    additionalContent,
    headerBadge,
    headerTitle,
    headerSubtitle,
    isSelector = false,
    selectorItems = [],
    headerAction,
    filterSelectorItem,
    selectorResultCap,
    selectorEmptyText,
  } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const modalRef = useRef<HTMLIonModalElement>(null);
  // The native app doesn't resize the screen for the keyboard, so a bottom sheet with a search box would
  // end up behind it — lift the sheet (and shrink its height limits) by the keyboard's height instead
  const keyboardHeight = useKeyboardHeight();

  // The native webview scrolls the whole page up to reveal a focused field, and Ionic positions overlays absolutely
  // inside ion-app, so the sheet went up with it and ended off the top of the screen. While a sheet is open, put the
  // page's own scroll straight back to the top (the sheet's list and the content behind scroll inside their own boxes).
  useEffect(() => {
    if (!isOpen) return;
    const app = document.querySelector('ion-app');
    const reset = () => {
      document.scrollingElement?.scrollTo(0, 0);
      app?.scrollTo(0, 0);
    };
    reset();
    document.addEventListener('scroll', reset, { passive: true });
    app?.addEventListener('scroll', reset, { passive: true });
    return () => {
      document.removeEventListener('scroll', reset);
      app?.removeEventListener('scroll', reset);
    };
  }, [isOpen]);

  // Scrolling the list must not drag the sheet closed when it reaches the top (see useSheetScrollGuard)
  const bodyRef = useSheetScrollGuard();

  // Show the drag handle only for the rich (booking) variant.
  const [handleEnabled, setHandleEnabled] = useState(!!headerTitle);
  const [prevSheetOpenKey, setPrevSheetOpenKey] = useState({ isOpen, headerTitle });
  if (prevSheetOpenKey.isOpen !== isOpen || prevSheetOpenKey.headerTitle !== headerTitle) {
    setPrevSheetOpenKey({ isOpen, headerTitle });
    if (isOpen) setHandleEnabled(!!headerTitle);
  }

  // Filter selector items based on search term
  const matchedSelectorItems = selectorItems.filter(item =>
    filterSelectorItem ? filterSelectorItem(item, searchTerm) : item.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const selectorCap = selectorResultCap?.(searchTerm.trim() !== '');
  const filteredSelectorItems = selectorCap ? matchedSelectorItems.slice(0, selectorCap) : matchedSelectorItems;

  const handleSelectorItemClick = (item: string) => {
    if (onSelectorItemClick) {
      onSelectorItemClick(item);
    }
    onClose();
  };

  const renderContent = () => {
    if (additionalContent) {
      return additionalContent;
    }

    if (isSelector && selectorItems.length > 0) {
      return (
        <>
          <IonSearchbar
            value={searchTerm}
            onIonInput={(e) => {
              const target = e.target as HTMLIonSearchbarElement;
              setSearchTerm(target.value || '');
            }}
            placeholder={searchPlaceholder}
            className={styles.searchBar}
          />
          <IonList>
            {filteredSelectorItems.map((item, index) => (
              <IonItem
                key={index}
                button
                detail={false}
                lines="none"
                onClick={() => handleSelectorItemClick(item)}
                className={styles.content}
              >
                {renderSelectorItem ? renderSelectorItem(item, index) : (
                  <IonLabel>{item}</IonLabel>
                )}
              </IonItem>
            ))}
          </IonList>
        </>
      );
    }

    // Default menu options rendering
    return (
      <IonList>
        {menuOptions.map((option, index) => {
          const chipInfo = showChips ? extractChipInfo(option.labelContent) : null;
          return (
            <IonItem 
              key={index} 
              button 
              onClick={option.onClick} 
              className={styles.content}
              detail={false}
              lines="none"
            >
              {option.customContent && option.customContent}
              {option.icon && <IonIcon icon={option.icon} slot="start" color={option.iconColor || option.color || 'primary'} />}
              <IonLabel>{option.label}</IonLabel>
              {showChips && chipInfo && (
                <IonChip color={chipInfo.color || option.color || 'primary'} slot="end">
                  <IonLabel>{chipInfo.count}</IonLabel>
                </IonChip>
              )}
              {option.rightIcon && <IonIcon icon={option.rightIcon} slot="end" color="success" />}
            </IonItem>
          );
        })}
      </IonList>
    );
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={() => { setSearchTerm(''); onClose(); }}
      className={styles.sheetModal}
      style={{ '--sheet-keyboard-lift': `${keyboardHeight}px` } as React.CSSProperties}
      breakpoints={[0, 1]}
      initialBreakpoint={1}
      backdropDismiss={true}
      handle={handleEnabled}
      leaveAnimation={sheetLeaveAnimation}
    >
      <IonHeader className='ion-no-border'>
        <IonToolbar className={headerTitle ? styles.richToolbar : undefined}>
          {headerTitle ? (
            <>
              <IonLabel className={styles.richHeader}>
                <IonText className={styles.headerTitleText}>{headerTitle}</IonText>
                {headerSubtitle && (
                  <IonText className={styles.headerSubtitleText} color="medium">{headerSubtitle}</IonText>
                )}
              </IonLabel>
              {headerBadge && (
                <IonBadge
                  slot="end"
                  className={`${styles.headerBadge} ${styles[BADGE_TONE_CLASS[headerBadge.color] || 'badgeMedium']}`}
                >
                  {headerBadge.label}
                </IonBadge>
              )}
            </>
          ) : (
            // Header content in an IonItem (normal flow), NOT IonTitle —
            // IonTitle is absolutely positioned and gave the toolbar no real
            // height in the sheet's flex column, leaving a tall empty gap on
            // native. Matches LabelModals' working sheet.
            <IonItem lines="none" className={styles.headerRow}>
              <IonLabel>{title}</IonLabel>
              {headerAction && (
                <IonButton fill="clear" slot="end" onClick={headerAction.onClick} className={styles.headerCloseBtn} aria-label={headerAction.label}>
                  <IonIcon icon={headerAction.icon} slot="icon-only" />
                </IonButton>
              )}
              <IonButton fill="clear" slot="end" onClick={onClose} className={styles.headerCloseBtn}>
                <IonIcon icon={closeOutline} slot="icon-only" />
              </IonButton>
            </IonItem>
          )}
        </IonToolbar>
      </IonHeader>

      {isSelector ? (
        <>
          <IonSearchbar
            value={searchTerm}
            onIonInput={(e) => {
              const target = e.target as HTMLIonSearchbarElement;
              setSearchTerm(target.value || '');
            }}
            placeholder={searchPlaceholder}
            className={styles.fixedSearchBar}
          />
          <IonCardContent ref={bodyRef} className={styles.selectorContent}>
            <IonList>
              {filteredSelectorItems.map((item, index) => (
                <IonItem
                  key={index}
                  button
                  detail={false}
                  lines="none"
                  onClick={() => handleSelectorItemClick(item)}
                  className={styles.content}
                >
                  {renderSelectorItem ? renderSelectorItem(item, index) : (
                    <IonLabel>{item}</IonLabel>
                  )}
                </IonItem>
              ))}
              {filteredSelectorItems.length === 0 && selectorEmptyText && (
                <IonItem lines="none" className={styles.content}>
                  <IonLabel className="ion-text-center" color="medium">{selectorEmptyText}</IonLabel>
                </IonItem>
              )}
            </IonList>
          </IonCardContent>
        </>
      ) : (
        <IonCardContent ref={bodyRef}>
          {renderContent()}
        </IonCardContent>
      )}
    </IonModal>
  );
};

export default SheetModal;